import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  Trophy,
  Download,
  Users,
  User,
  CreditCard,
  Check,
  CheckCircle2,
  Clock,
  Receipt,
  Wallet,
  TrendingUp,
  TrendingDown,
  X,
  Image as ImageIcon,
  MessageCircle,
  Pencil,
  Plus,
  Trash,
} from "lucide-react";
import moment from "moment";
import { getTournamentReport, getTournamentsByDate, updateTeamMemberStatus } from "../../api/tournaments";
import { confirmInstaPayRegistration } from "../../api/payments";
import {
  getExpenseCategories,
  createTournamentExpense,
  updateTournamentExpense,
  deleteTournamentExpense,
  ExpenseCategoryDTO,
} from "../../api/expenses";
import { useAppSelector, useAppDispatch } from "../../store/store";
import { isSuperAdmin, isPadelReportsViewerOnly } from "../../utils/authorization";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import {
  TournamentReportRegistrationDTO,
  TournamentReportPaymentDTO,
  TournamentReportExpenseDTO,
  TournamentReportSummaryDTO,
} from "../../types/tournament";
import {
  Alert,
  Button,
  buttonClassName,
  Card,
  ConfirmDialog,
  DateInput,
  DatePicker,
  DescriptionList,
  Dialog,
  EmptyState,
  Field,
  FormGrid,
  IconButton,
  Input,
  PageHeader,
  SearchInput,
  Select,
  SkeletonRows,
  StatCard,
  TableWrap,
  Tabs,
  Tag,
  Toolbar,
} from "../../ui";
import type { DescriptionItem, TagTone } from "../../ui";
import "./styles.scss";

type Tab = "registrations" | "payments" | "expenses";

type PaymentSource = "Online" | "InstaPay" | "Balance" | "Cash";

// Aliases so the rest of the file doesn't need to know about the DTO names.
type RegistrationRow = TournamentReportRegistrationDTO;
type PaymentRow = TournamentReportPaymentDTO;
type ExpenseRow = TournamentReportExpenseDTO;

// Discriminated union for the right drawer — lets us share one selection state
// across the three tab types.
type DrawerSelection =
  | { kind: "registration"; row: RegistrationRow }
  | { kind: "payment"; row: PaymentRow }
  | { kind: "expense"; row: ExpenseRow };

type ExpenseDialogState = { mode: "create" } | { mode: "edit"; row: ExpenseRow };

// Keeps the last non-null value, so a dialog still has its subject while it plays its closing animation.
function useLastDefined<T>(value: T | null): T | null {
  const last = useRef<T | null>(value);
  if (value !== null) last.current = value;
  return last.current;
}

// Cairo-time formatters used across the page so timestamps match the rest of the dashboard.
const cairoDateTimeFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit", month: "short", year: "numeric",
  hour: "2-digit", minute: "2-digit", hour12: true,
  timeZone: "Africa/Cairo",
});
const cairoDateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit", month: "short", year: "numeric",
  timeZone: "Africa/Cairo",
});
const fmtCairoDateTime = (v: string | Date | null | undefined) =>
  v ? cairoDateTimeFmt.format(new Date(v)) : "-";
const fmtCairoDate = (v: string | Date | null | undefined) =>
  v ? cairoDateFmt.format(new Date(v)) : "-";

// Registration fees can be priced in another currency (e.g. RUB tournaments);
// a missing currency means EGP. Expenses are always EGP.
const currencyCode = (currency?: string | null) => (currency || "").trim().toUpperCase() || "EGP";
const formatAmount = (n: number) =>
  Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatMoney = (n: number, currency?: string | null) => formatAmount(n) + " " + currencyCode(currency);
const formatEGP = (n: number) => formatMoney(n, "EGP");

// A per-team fee is booked once, on the payer's row; teammates' rows owe 0.
const isTeamFeeShare = (r: { feePerTeam?: boolean; feeAmount: number; feePrice?: number | null }) =>
  !!r.feePerTeam && r.feeAmount === 0 && !!r.feePrice;

// Build a wa.me link from a phone number. Strips non-digits and assumes
// Egyptian "01…" (11 digits) is local — prepends country code 20 so the
// deep link opens with the right international number. Anything else passes
// through as-is on the assumption it's already international.
const whatsappLink = (phone: string | null | undefined): string | null => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (!digits) return null;
  let normalized = digits;
  if (normalized.startsWith("00")) normalized = normalized.slice(2);
  else if (normalized.startsWith("0") && normalized.length === 11) normalized = "20" + normalized.slice(1);
  return `https://wa.me/${normalized}`;
};

// Reservation duration as a friendly "Xh", "Xh Ym", or "Ym" label. Negative or
// zero ranges fall back to "—" so dirty data doesn't render "0h".
const formatHours = (start: string | Date, end: string | Date) => {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!isFinite(ms) || ms <= 0) return "—";
  const totalMinutes = Math.round(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const csvCell = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

type CsvColumn<T> = { header: string; value: (row: T) => unknown };

const downloadCsv = <T,>(rows: T[], columns: CsvColumn<T>[], filename: string) => {
  const lines = [
    columns.map((c) => csvCell(c.header)).join(","),
    ...rows.map((row) => columns.map((c) => csvCell(c.value(row))).join(",")),
  ];
  // BOM keeps Excel happy with UTF-8 (Arabic player names render correctly).
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const todayStamp = () => moment().format("YYYY-MM-DD");

const STATUS_TONES: Record<string, TagTone> = {
  Approved: "ok", Paid: "ok", Confirmed: "ok",
  Pending: "warn", Initiated: "warn",
  "Pending Payment": "warn", "Pending Consent": "warn", "Pending Invite": "warn",
  Rejected: "danger", Failed: "danger", Canceled: "danger", Cancelled: "danger",
  Expired: "neutral",
  Refunded: "info",
};

const StatusBadge = ({ status }: { status: string | null | undefined }) => {
  if (!status) return <span className="rep-cell-muted">-</span>;
  return <Tag tone={STATUS_TONES[status] || "neutral"}>{status}</Tag>;
};

const SOURCE_TONES: Record<string, TagTone> = { Online: "brand", InstaPay: "spark", Balance: "warn", Cash: "neutral" };

const SourceTag = ({ source }: { source: string }) => (
  <Tag tone={SOURCE_TONES[source] || "brand"}>{source === "Balance" ? "Wallet" : source}</Tag>
);

// A clickable table row: opens the drawer on click, Enter or Space (but not when the key comes from a button inside it).
const rowProps = (onOpen: () => void, selected: boolean) => ({
  className: ["is-clickable", selected ? "is-selected" : ""].filter(Boolean).join(" "),
  tabIndex: 0,
  onClick: onOpen,
  onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    onOpen();
  },
});

// A labelled group of controls in the tournament picker.
const FilterGroup = ({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) => (
  <div className={["rep-filter", className].filter(Boolean).join(" ")} role="group" aria-label={label}>
    <span className="rep-filter__label">{label}</span>
    <div className="rep-filter__controls">{children}</div>
  </div>
);

// Date-then-tournament picker. Same chained pattern as the Transactions page:
// pick a date first, then choose from the tournaments active on that date.
// Necessary because tournaments can share names across editions, so a name-only
// search is ambiguous.
type TournamentByDateOption = { id: string; name: string; startDate: string; endDate: string };

const TournamentSelector = ({
  selectedDate,
  selectedId,
  onDateChange,
  onSelect,
}: {
  selectedDate: string;
  selectedId: string;
  onDateChange: (date: string) => void;
  onSelect: (t: TournamentByDateOption | null) => void;
}) => {
  const [tournaments, setTournaments] = useState<TournamentByDateOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Single backend call returns every tournament whose date window covers
  // the selected day — no client-side filtering, no pagination concerns.
  useEffect(() => {
    if (!selectedDate) {
      setTournaments([]);
      return;
    }
    setLoading(true);
    getTournamentsByDate(selectedDate)
      .then(({ data }) => setTournaments(data || []))
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  return (
    <>
      <FilterGroup label="Date">
        <div className="rep-date-filter">
          <DatePicker
            selected={selectedDate ? moment(selectedDate).toDate() : null}
            onChange={(date) => onDateChange(date ? moment(date).format("YYYY-MM-DD") : "")}
            dateFormat="dd MMM yyyy"
            placeholderText="Pick a date"
            aria-label="Tournament date"
            isClearable
          />
        </div>
      </FilterGroup>
      {selectedDate && (
        <FilterGroup label="Tournament">
          <Select
            className="rep-tournament-select"
            aria-label="Tournament"
            value={selectedId}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) {
                onSelect(null);
                return;
              }
              const t = tournaments.find((x) => x.id === id);
              if (t) onSelect(t);
            }}
            disabled={loading || tournaments.length === 0}
          >
            <option value="">
              {loading
                ? "Loading tournaments..."
                : tournaments.length === 0
                ? "No tournaments on this date"
                : "Select a tournament"}
            </option>
            {tournaments.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.endDate && t.endDate !== t.startDate
                  ? ` (${fmtCairoDate(t.startDate)} – ${fmtCairoDate(t.endDate)})`
                  : ` (${fmtCairoDate(t.startDate)})`}
              </option>
            ))}
          </Select>
        </FilterGroup>
      )}
    </>
  );
};

const Reports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tournamentDate = searchParams.get("tournamentDate") || "";
  const tournamentId = searchParams.get("tournamentId") || "";
  const tournamentName = searchParams.get("tournamentName") || "";
  const tournamentStart = searchParams.get("tournamentStart") || "";
  const tournamentEnd = searchParams.get("tournamentEnd") || "";
  const activeTab = (searchParams.get("tab") as Tab) || "registrations";

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((s) => s.auth.currentUser);
  const canManageExpenses = isSuperAdmin(currentUser);
  // Padel-only viewers see aggregates only — hide tabs, registrations, payments,
  // expenses (the backend redacts the underlying lists too).
  const padelOnlyView = isPadelReportsViewerOnly(currentUser);
  const [registrations, setRegistrations] = useState<RegistrationRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategoryDTO[]>([]);
  const [expenseDialog, setExpenseDialog] = useState<ExpenseDialogState | null>(null);
  // Bumped on every open so the form starts from the row (or blank) each time.
  const [expenseFormKey, setExpenseFormKey] = useState(0);
  const [expenseToDelete, setExpenseToDelete] = useState<ExpenseRow | null>(null);
  const [expenseBusy, setExpenseBusy] = useState(false);
  const [serverSummary, setServerSummary] = useState<TournamentReportSummaryDTO | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<PaymentRow | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Row clicked to open the right detail drawer. One state for all three tabs
  // — discriminated by `kind` so we don't have to juggle separate selections.
  const [drawerSelection, setDrawerSelection] = useState<DrawerSelection | null>(null);

  // The dialogs keep their last subject while they animate out.
  const drawerShown = useLastDefined(drawerSelection);
  const confirmShown = useLastDefined(pendingConfirm);
  const expenseDialogShown = useLastDefined(expenseDialog);
  const deleteShown = useLastDefined(expenseToDelete);

  // Per-tab in-memory search to keep the page snappy without paginating.
  const [regSearch, setRegSearch] = useState("");
  const [regStatusFilter, setRegStatusFilter] = useState("");
  const [regPaymentFilter, setRegPaymentFilter] = useState("");
  const [regCategoryFilter, setRegCategoryFilter] = useState("");
  const [paySearch, setPaySearch] = useState("");
  const [paySourceFilter, setPaySourceFilter] = useState<"" | PaymentSource>("");
  const [payStatusFilter, setPayStatusFilter] = useState("");
  const [expSearch, setExpSearch] = useState("");
  const [expStatusFilter, setExpStatusFilter] = useState("");

  useEffect(() => { document.title = "Tournament Reports"; }, []);

  // Categories are needed for the manual expense form. Load once on mount —
  // they're a small lookup and rarely change.
  useEffect(() => {
    if (!canManageExpenses) return;
    let cancelled = false;
    getExpenseCategories()
      .then((res: any) => {
        if (cancelled) return;
        const items = res?.data?.data ?? res?.data ?? [];
        setExpenseCategories(Array.isArray(items) ? items : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canManageExpenses]);

  // Bumped after a successful confirm to refetch the report. Cheaper than
  // mutating the in-memory list since the backend recomputes the summary too.
  const [reloadKey, setReloadKey] = useState(0);

  // Single backend round trip — registrations + payments + summary come from
  // GET /Tournament/{id}/report. Replaces the old fan-out across category
  // detail and payment endpoints.
  useEffect(() => {
    if (!tournamentId) {
      setRegistrations([]);
      setPayments([]);
      setExpenses([]);
      setServerSummary(null);
      return;
    }
    let cancelled = false;
    setReportLoading(true);
    getTournamentReport(tournamentId)
      .then(({ data: report }) => {
        if (cancelled) return;
        setRegistrations(report?.registrations || []);
        setPayments(report?.payments || []);
        setExpenses(report?.expenses || []);
        setServerSummary(report?.summary || null);
      })
      .catch((err) => {
        console.error("[Reports] failed to load tournament report", err);
        if (cancelled) return;
        setRegistrations([]);
        setPayments([]);
        setExpenses([]);
        setServerSummary(null);
      })
      .finally(() => { if (!cancelled) setReportLoading(false); });
    return () => { cancelled = true; };
  }, [tournamentId, reloadKey]);

  // Apply admin edits to a registration (status/payment/method) made from
  // inside the detail drawer. Reuses the same backend endpoint the
  // tournament-category dialog uses, so behavior matches across surfaces.
  const handleUpdateRegistration = async (
    teamId: string,
    teamMemberId: string,
    payload: {
      status?: string;
      paymentStatus?: string;
      paymentMethod?: string | null;
    }
  ) => {
    try {
      await updateTeamMemberStatus(teamId, teamMemberId, payload);
      dispatch(setGeneralAlertMessage({ type: "success", message: "Registration updated" }));
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to update registration" }));
      throw err;
    }
  };

  // InstaPay confirm — only the payment id is shaped as `instapay:<teamMemberId>`.
  // Online (Fawry) payments are settled via the gateway callback and don't need
  // a manual confirm path here.
  const handleConfirmPayment = async () => {
    if (!pendingConfirm) return;
    if (pendingConfirm.source !== "InstaPay") return;
    const teamMemberId = pendingConfirm.id.replace(/^instapay:/, "");
    if (!teamMemberId) return;
    setConfirmingId(pendingConfirm.id);
    try {
      await confirmInstaPayRegistration(teamMemberId);
      dispatch(setGeneralAlertMessage({ type: "success", message: "Payment confirmed" }));
      setPendingConfirm(null);
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to confirm payment" }));
    } finally {
      setConfirmingId(null);
    }
  };

  const handleDateChange = (date: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (date) p.set("tournamentDate", date);
      else p.delete("tournamentDate");
      // Changing the date invalidates the tournament selection below it.
      p.delete("tournamentId");
      p.delete("tournamentName");
      p.delete("tournamentStart");
      p.delete("tournamentEnd");
      return p;
    });
  };

  const handleSelectTournament = (t: TournamentByDateOption | null) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (!t) {
        p.delete("tournamentId");
        p.delete("tournamentName");
        p.delete("tournamentStart");
        p.delete("tournamentEnd");
        return p;
      }
      p.set("tournamentId", t.id);
      p.set("tournamentName", t.name);
      p.set("tournamentStart", t.startDate);
      if (t.endDate) p.set("tournamentEnd", t.endDate);
      if (!p.get("tab")) p.set("tab", "registrations");
      return p;
    });
  };

  const setTab = (tab: Tab) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", tab);
      return p;
    });
  };

  // Distinct list of categories present in this tournament's registrations,
  // used to populate the category filter dropdown.
  const categoryOptions = useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return Array.from(new Set(registrations.map((r) => r.categoryName).filter(Boolean))).sort((a, b) =>
      collator.compare(a, b)
    );
  }, [registrations]);

  // ── Filtered views ──
  const filteredRegistrations = useMemo(() => {
    const q = regSearch.trim().toLowerCase();
    const filtered = registrations.filter((r) => {
      if (regCategoryFilter && r.categoryName !== regCategoryFilter) return false;
      if (regStatusFilter && r.registrationStatus !== regStatusFilter) return false;
      if (regPaymentFilter && r.paymentStatus !== regPaymentFilter) return false;
      if (!q) return true;
      return (
        r.playerName.toLowerCase().includes(q) ||
        r.teamName.toLowerCase().includes(q) ||
        r.categoryName.toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.phoneNumber || "").toLowerCase().includes(q)
      );
    });
    // Group registrations by team — captains float to the top of each team so
    // it's obvious who owns the registration.
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return [...filtered].sort((a, b) => {
      const byTeam = collator.compare(a.teamName, b.teamName);
      if (byTeam !== 0) return byTeam;
      if (a.isCaptain !== b.isCaptain) return a.isCaptain ? -1 : 1;
      return collator.compare(a.playerName, b.playerName);
    });
  }, [registrations, regSearch, regStatusFilter, regPaymentFilter, regCategoryFilter]);

  const filteredPayments = useMemo(() => {
    const q = paySearch.trim().toLowerCase();
    return payments.filter((r) => {
      if (paySourceFilter && r.source !== paySourceFilter) return false;
      if (payStatusFilter && r.paymentStatus !== payStatusFilter) return false;
      if (!q) return true;
      return (
        r.playerName.toLowerCase().includes(q) ||
        r.teamName.toLowerCase().includes(q) ||
        (r.categoryName || "").toLowerCase().includes(q) ||
        (r.reference || "").toLowerCase().includes(q)
      );
    });
  }, [payments, paySearch, paySourceFilter, payStatusFilter]);

  // Server-computed summary, with a client fallback that matches the backend's
  // count rules (Pending = payment status pending, not registration status).
  const summary = useMemo<TournamentReportSummaryDTO>(() => {
    if (serverSummary) return serverSummary;
    const totalPaid = payments.filter((p) => p.paymentStatus === "Paid").reduce((s, p) => s + Number(p.amount || 0), 0);
    const totalExpenses = expenses.reduce((s, e) => s + Number(e.cost || 0), 0);
    const pendingAmount = registrations
      .filter((r) => r.paymentStatus === "Pending")
      .reduce((s, r) => s + Number(r.feeAmount || 0), 0);
    const expectedTotal = registrations
      .filter((r) => r.registrationStatus !== "Canceled")
      .reduce((s, r) => s + Number(r.feeAmount || 0), 0);
    return {
      teams: new Set(registrations.map((r) => r.teamId)).size,
      players: registrations.length,
      confirmed: registrations.filter((r) => r.registrationStatus === "Confirmed").length,
      pending: registrations.filter((r) => r.paymentStatus === "Pending").length,
      totalPaid,
      pendingAmount,
      expectedTotal,
      totalExpenses,
      netRevenue: totalPaid - totalExpenses,
    };
  }, [serverSummary, registrations, payments, expenses]);

  const filteredExpenses = useMemo(() => {
    const q = expSearch.trim().toLowerCase();
    const filtered = expenses.filter((e) => {
      // Status filter only applies to court-reservation rows; manual rows
      // have no status, so when a status is picked, hide them.
      if (expStatusFilter) {
        if (e.source !== "CourtReservation") return false;
        if (e.status !== expStatusFilter) return false;
      }
      if (!q) return true;
      return (
        (e.courtName || "").toLowerCase().includes(q) ||
        (e.description || "").toLowerCase().includes(q) ||
        (e.categoryName || "").toLowerCase().includes(q) ||
        (e.notes || "").toLowerCase().includes(q) ||
        (e.paymentMethod || "").toLowerCase().includes(q)
      );
    });
    // Sort by date descending — manual rows by expenseDate, court rows by startTime.
    return [...filtered].sort((a, b) => {
      const aDate = a.expenseDate || a.startTime || "";
      const bDate = b.expenseDate || b.startTime || "";
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
  }, [expenses, expSearch, expStatusFilter]);

  const openExpenseDialog = (state: ExpenseDialogState) => {
    setDrawerSelection(null);
    setExpenseFormKey((k) => k + 1);
    setExpenseDialog(state);
  };

  const handleSaveExpense = async (payload: {
    categoryId: string;
    amount: number;
    description?: string;
    status?: string;
    paymentMethod?: string;
    expenseDate?: string;
  }) => {
    if (!tournamentId || !expenseDialog) return;
    setExpenseBusy(true);
    try {
      if (expenseDialog.mode === "create") {
        await createTournamentExpense(tournamentId, payload);
        dispatch(setGeneralAlertMessage({ type: "success", message: "Expense added" }));
      } else {
        await updateTournamentExpense(tournamentId, expenseDialog.row.id, payload);
        dispatch(setGeneralAlertMessage({ type: "success", message: "Expense updated" }));
      }
      setExpenseDialog(null);
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to save expense" }));
    } finally {
      setExpenseBusy(false);
    }
  };

  const handleDeleteExpense = async () => {
    if (!tournamentId || !expenseToDelete) return;
    setExpenseBusy(true);
    try {
      await deleteTournamentExpense(tournamentId, expenseToDelete.id);
      dispatch(setGeneralAlertMessage({ type: "success", message: "Expense deleted" }));
      setExpenseToDelete(null);
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to delete expense" }));
    } finally {
      setExpenseBusy(false);
    }
  };

  const tabs: { key: Tab; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: "registrations", label: "Registrations", icon: <Users size={15} />, count: registrations.length },
    { key: "payments", label: "Payments", icon: <CreditCard size={15} />, count: payments.length },
    { key: "expenses", label: "Expenses", icon: <Receipt size={15} />, count: expenses.length },
  ];

  const exportRegistrations = () => {
    downloadCsv<RegistrationRow>(
      filteredRegistrations,
      [
        { header: "Player", value: (r) => r.playerName },
        { header: "Team", value: (r) => r.teamName },
        { header: "Category", value: (r) => r.categoryName },
        { header: "Captain", value: (r) => r.isCaptain ? "Yes" : "No" },
        { header: "Registration Status", value: (r) => r.registrationStatus },
        { header: "Payment Status", value: (r) => r.paymentStatus },
        { header: "Amount Due", value: (r) => r.feeAmount.toFixed(2) },
        { header: "Fee Price", value: (r) => (r.feePrice != null ? r.feePrice.toFixed(2) : "") },
        { header: "Currency", value: (r) => r.feeCurrency || "" },
        { header: "Fee Description", value: (r) => r.feeDescription || "" },
        { header: "Per Team Fee", value: (r) => (r.feePerTeam ? "Yes" : "No") },
        { header: "Promo Code", value: (r) => r.promoCode || "" },
        { header: "Payment Method", value: (r) => r.paymentMethod || "" },
        { header: "Fawry Ref", value: (r) => r.fawryReferenceNumber || "" },
        { header: "Email", value: (r) => r.email || "" },
        { header: "Phone", value: (r) => r.phoneNumber || "" },
        { header: "Nationality", value: (r) => r.nationality || "" },
        { header: "Gender", value: (r) => r.gender || "" },
        { header: "Consent", value: (r) => r.consent ? "Yes" : "No" },
        { header: "Registered At (Cairo)", value: (r) => fmtCairoDateTime(r.addedAt) },
      ],
      `registrations-${slugify(tournamentName)}-${todayStamp()}.csv`,
    );
  };

  const exportPayments = () => {
    downloadCsv<PaymentRow>(
      filteredPayments,
      [
        { header: "Source", value: (r) => r.source },
        { header: "Player", value: (r) => r.playerName },
        { header: "Team", value: (r) => r.teamName },
        { header: "Category", value: (r) => r.categoryName || "" },
        { header: "Pay For Team", value: (r) => r.payForTeam ? "Yes" : "No" },
        { header: "Team Member Count", value: (r) => r.teamMemberCount ?? "" },
        { header: "Amount", value: (r) => r.amount.toFixed(2) },
        { header: "Currency", value: (r) => r.currency || "EGP" },
        { header: "Payment Status", value: (r) => r.paymentStatus },
        { header: "Registration Status", value: (r) => r.registrationStatus || "" },
        { header: "Method", value: (r) => r.paymentMethod || "" },
        { header: "Reference", value: (r) => r.reference || "" },
        { header: "Receipt URL", value: (r) => r.receiptScreenshot || "" },
        { header: "Created (Cairo)", value: (r) => fmtCairoDateTime(r.createdAt) },
      ],
      `payments-${slugify(tournamentName)}-${todayStamp()}.csv`,
    );
  };

  const exportExpenses = () => {
    downloadCsv<ExpenseRow>(
      filteredExpenses,
      [
        { header: "Type", value: (r) => r.source === "Manual" ? "Manual" : "Court Reservation" },
        { header: "Category", value: (r) => r.categoryName || "" },
        { header: "Court / Description", value: (r) => r.source === "Manual" ? (r.description || "") : (r.courtName || "") },
        { header: "Date (Cairo)", value: (r) => fmtCairoDateTime(r.expenseDate || r.startTime) },
        { header: "End (Cairo)", value: (r) => r.endTime ? fmtCairoDateTime(r.endTime) : "" },
        { header: "Hours", value: (r) => r.startTime && r.endTime ? formatHours(r.startTime, r.endTime) : "" },
        { header: "Cost (EGP)", value: (r) => Number(r.cost).toFixed(2) },
        { header: "Status", value: (r) => r.status || "" },
        { header: "Method", value: (r) => r.paymentMethod || "" },
        { header: "Notes", value: (r) => r.notes || "" },
      ],
      `expenses-${slugify(tournamentName)}-${todayStamp()}.csv`,
    );
  };

  return (
    <div className="reports-page">
      <PageHeader title="Tournament Reports" subtitle="Pick a tournament to see every registration and payment in one place" />

      <Toolbar
        className="rep-selector"
        actions={
          tournamentId && tournamentName ? (
            <div className="rep-selected">
              <Trophy size={16} className="rep-selected__icon" aria-hidden="true" />
              <div className="rep-selected__text">
                <span className="rep-selected__name">{tournamentName}</span>
                {tournamentStart && (
                  <span className="rep-selected__dates">
                    {fmtCairoDate(tournamentStart)}
                    {tournamentEnd && tournamentEnd !== tournamentStart ? ` – ${fmtCairoDate(tournamentEnd)}` : ""}
                  </span>
                )}
              </div>
            </div>
          ) : undefined
        }
      >
        <TournamentSelector
          selectedDate={tournamentDate}
          selectedId={tournamentId}
          onDateChange={handleDateChange}
          onSelect={handleSelectTournament}
        />
      </Toolbar>

      {!tournamentId ? (
        <Card padding="none">
          <EmptyState
            icon={<Trophy />}
            title={tournamentDate ? "Select a tournament" : "Pick a date to begin"}
            description={
              tournamentDate
                ? "Choose one of the tournaments active on the selected date."
                : "Pick a date first, then choose the tournament whose report you want to view."
            }
          />
        </Card>
      ) : (
        <>
          <div className="rep-summary">
            <div className="rep-summary__row">
              {!padelOnlyView && (
                <StatCard label="Teams" value={summary.teams.toLocaleString()} icon={<Users />} tone="neutral" />
              )}
              <StatCard label="Players" value={summary.players.toLocaleString()} icon={<User />} tone="brand" />
              <StatCard label="Confirmed" value={summary.confirmed.toLocaleString()} icon={<CheckCircle2 />} tone="ok" />
              <StatCard
                label="Pending"
                value={summary.pending.toLocaleString()}
                icon={<Clock />}
                tone="spark"
                className="rep-stat--pending"
                hint={summary.pendingAmount > 0 ? formatMoney(summary.pendingAmount, summary.currency) + " owed" : undefined}
              />
            </div>
            <div className="rep-summary__row rep-summary__row--money">
              <StatCard
                label="Total Paid"
                value={<MoneyValue amount={summary.totalPaid} currency={summary.currency} />}
                icon={<Wallet />}
                tone="ok"
                className="rep-stat--money"
                hint={summary.expectedTotal > 0 ? `of ${formatMoney(summary.expectedTotal, summary.currency)} expected` : undefined}
              />
              <StatCard
                label="Expenses"
                value={<MoneyValue amount={summary.totalExpenses} />}
                icon={<Receipt />}
                tone="danger"
                className="rep-stat--money"
              />
              <StatCard
                label="Net Revenue"
                value={<MoneyValue amount={summary.netRevenue} />}
                icon={summary.netRevenue >= 0 ? <TrendingUp /> : <TrendingDown />}
                tone={summary.netRevenue >= 0 ? "ok" : "danger"}
                className={`rep-stat--money ${summary.netRevenue >= 0 ? "rep-stat--gain" : "rep-stat--loss"}`}
              />
            </div>
          </div>

          {!padelOnlyView && (
            <Tabs
              className="rep-tabs"
              ariaLabel="Report sections"
              options={tabs.map((t) => ({ value: t.key, label: t.label, icon: t.icon, count: t.count }))}
              value={activeTab}
              onChange={setTab}
            />
          )}

          {!padelOnlyView && (
          <div className="rep-tab-panel">
            {activeTab === "registrations" && (
              <RegistrationsPanel
                rows={filteredRegistrations}
                loading={reportLoading}
                searchValue={regSearch}
                onSearchChange={setRegSearch}
                statusFilter={regStatusFilter}
                onStatusChange={setRegStatusFilter}
                paymentFilter={regPaymentFilter}
                onPaymentChange={setRegPaymentFilter}
                categoryFilter={regCategoryFilter}
                onCategoryChange={setRegCategoryFilter}
                categoryOptions={categoryOptions}
                onExport={exportRegistrations}
                onRowClick={(row) => setDrawerSelection({ kind: "registration", row })}
                activeId={drawerSelection?.kind === "registration" ? drawerSelection.row.teamMemberId : null}
              />
            )}
            {activeTab === "payments" && (
              <PaymentsPanel
                rows={filteredPayments}
                loading={reportLoading}
                searchValue={paySearch}
                onSearchChange={setPaySearch}
                sourceFilter={paySourceFilter}
                onSourceChange={setPaySourceFilter}
                statusFilter={payStatusFilter}
                onStatusChange={setPayStatusFilter}
                onExport={exportPayments}
                onRowClick={(row) => setDrawerSelection({ kind: "payment", row })}
                activeId={drawerSelection?.kind === "payment" ? drawerSelection.row.id : null}
                onPreviewReceipt={(url) => setPreviewUrl(url)}
              />
            )}
            {activeTab === "expenses" && (
              <ExpensesPanel
                rows={filteredExpenses}
                loading={reportLoading}
                searchValue={expSearch}
                onSearchChange={setExpSearch}
                statusFilter={expStatusFilter}
                onStatusChange={setExpStatusFilter}
                onExport={exportExpenses}
                onRowClick={(row) => setDrawerSelection({ kind: "expense", row })}
                activeId={drawerSelection?.kind === "expense" ? drawerSelection.row.id : null}
                canManage={canManageExpenses}
                onAddExpense={() => openExpenseDialog({ mode: "create" })}
                onEditExpense={(row) => openExpenseDialog({ mode: "edit", row })}
                onDeleteExpense={(row) => { setDrawerSelection(null); setExpenseToDelete(row); }}
              />
            )}
          </div>
          )}

        </>
      )}

      {drawerShown && (
        <DetailDrawer
          open={!!drawerSelection}
          selection={drawerShown}
          onClose={() => setDrawerSelection(null)}
          onRequestConfirm={(row) => setPendingConfirm(row)}
          confirmingId={confirmingId}
          onPreviewReceipt={(url) => setPreviewUrl(url)}
          onUpdateRegistration={handleUpdateRegistration}
        />
      )}
      {confirmShown && (
        <ConfirmPaymentDialog
          open={!!pendingConfirm}
          row={confirmShown}
          busy={confirmingId === confirmShown.id}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={handleConfirmPayment}
        />
      )}
      {previewUrl && (
        <ReceiptLightbox src={previewUrl} onClose={() => setPreviewUrl(null)} />
      )}
      {expenseDialogShown && (
        <ExpenseFormDialog
          key={expenseFormKey}
          open={!!expenseDialog}
          mode={expenseDialogShown.mode}
          initial={expenseDialogShown.mode === "edit" ? expenseDialogShown.row : undefined}
          categories={expenseCategories}
          busy={expenseBusy}
          onCancel={() => setExpenseDialog(null)}
          onSave={handleSaveExpense}
        />
      )}
      {deleteShown && (
        <DeleteExpenseDialog
          open={!!expenseToDelete}
          row={deleteShown}
          busy={expenseBusy}
          onCancel={() => setExpenseToDelete(null)}
          onConfirm={handleDeleteExpense}
        />
      )}
    </div>
  );
};

// A money figure in a stat card: the number in the display font, the currency code small after it.
const MoneyValue = ({ amount, currency }: { amount: number; currency?: string | null }) => (
  <>
    {formatAmount(amount)}
    <span className="rep-stat__currency">{currencyCode(currency)}</span>
  </>
);

// Full-screen receipt preview: keeps the admin on the report page instead of opening the receipt in a new tab, with
// the original one click away. Closes on Escape, a backdrop click or the close button. Escape is caught before the
// drawer under it sees it, so only the preview closes.
const ReceiptLightbox = ({ src, onClose }: { src: string; onClose: () => void }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
  }, []);
  return createPortal(
    <div className="rep-lightbox" onClick={onClose} role="dialog" aria-modal="true" aria-label="Payment receipt">
      <button ref={closeRef} type="button" className="rep-lightbox-close" onClick={onClose} aria-label="Close">
        <X size={22} />
      </button>
      <img src={src} alt="Payment receipt" onClick={(e) => e.stopPropagation()} />
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="rep-lightbox-open"
        onClick={(e) => e.stopPropagation()}
      >
        Open original
      </a>
    </div>,
    document.body
  );
};

// Label / value pairs in a drawer section; falsy entries are left out.
const Fields = ({ items, columns = 2 }: { items: (DescriptionItem | false | null | undefined | "")[]; columns?: 1 | 2 }) => (
  <DescriptionList columns={columns} items={items.filter(Boolean) as DescriptionItem[]} />
);

const DrawerSection = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <section className="rep-drawer-section">
    <h3 className="rep-drawer-section-label">{label}</h3>
    {children}
  </section>
);

const ReceiptButton = ({ url, onPreview }: { url: string; onPreview: (url: string) => void }) => (
  <IconButton variant="tint" label="Preview receipt" icon={<ImageIcon size={18} />} onClick={() => onPreview(url)} />
);

// Right-side drawer that surfaces every field for the clicked row plus any
// row-specific actions. Discriminates on `selection.kind` so registrations,
// payments, and expenses can each render their own field set.
const DetailDrawer = ({
  open,
  selection,
  onClose,
  onRequestConfirm,
  confirmingId,
  onPreviewReceipt,
  onUpdateRegistration,
}: {
  open: boolean;
  selection: DrawerSelection;
  onClose: () => void;
  onRequestConfirm: (row: PaymentRow) => void;
  confirmingId: string | null;
  onPreviewReceipt: (url: string) => void;
  onUpdateRegistration: (
    teamId: string,
    teamMemberId: string,
    payload: { status?: string; paymentStatus?: string; paymentMethod?: string | null }
  ) => Promise<void>;
}) => {
  // Registration-only edit state. Reset whenever the drawer points at a new
  // registration so stale form values don't bleed across selections.
  const [isEditing, setIsEditing] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [editPaymentStatus, setEditPaymentStatus] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    setIsEditing(false);
    if (selection.kind === "registration") {
      setEditStatus(selection.row.registrationStatus || "");
      setEditPaymentStatus(selection.row.paymentStatus || "");
      setEditPaymentMethod(selection.row.paymentMethod || "");
    }
  }, [selection]);

  let eyebrow = "";
  let title: React.ReactNode = null;
  let subtitle: React.ReactNode = null;
  let body: React.ReactNode = null;
  let actions: React.ReactNode = null;

  if (selection.kind === "registration") {
    const r = selection.row;
    eyebrow = "Registration";
    title = r.playerName;
    subtitle = (
      <>
        {r.teamName} · {r.categoryName}
        {r.isCaptain && <Tag tone="spark" className="rep-drawer-tag">Captain</Tag>}
      </>
    );
    // Gateway-set methods (fawry/balance) stay locked — admins shouldn't
    // overwrite the gateway's source of truth — so the dropdown only appears
    // for new/manual rows.
    const lockedMethod = r.paymentMethod === "fawry" || r.paymentMethod === "balance";
    const methodLabel = (m: string | null | undefined) =>
      m === "fawry" ? "Online (Fawry)" :
      m === "instapay" ? "InstaPay" :
      m === "cash" ? "Cash" :
      m === "balance" ? "Clash Balance" :
      m || "-";
    body = (
      <>
        <DrawerSection label="Status">
          {isEditing ? (
            <FormGrid columns={1}>
              <Field label="Registration">
                {(id) => (
                  <Select id={id} block value={editStatus} onChange={(e) => setEditStatus(e.target.value)} disabled={savingEdit}>
                    <option value="Pending Payment">Pending Payment</option>
                    <option value="Pending Consent">Pending Consent</option>
                    <option value="Pending Invite">Pending Invite</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Canceled">Canceled</option>
                  </Select>
                )}
              </Field>
              <Field label="Payment">
                {(id) => (
                  <Select id={id} block value={editPaymentStatus} onChange={(e) => setEditPaymentStatus(e.target.value)} disabled={savingEdit}>
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                  </Select>
                )}
              </Field>
              <Field label="Method">
                {(id) =>
                  lockedMethod ? (
                    <Input id={id} type="text" value={methodLabel(r.paymentMethod)} disabled readOnly />
                  ) : (
                    <Select id={id} block value={editPaymentMethod} onChange={(e) => setEditPaymentMethod(e.target.value)} disabled={savingEdit}>
                      <option value="">Not set</option>
                      <option value="instapay">InstaPay</option>
                      <option value="cash">Cash</option>
                    </Select>
                  )
                }
              </Field>
            </FormGrid>
          ) : (
            <Fields
              items={[
                { label: "Registration", value: <StatusBadge status={r.registrationStatus} /> },
                { label: "Payment", value: <StatusBadge status={r.paymentStatus} /> },
                { label: "Method", value: methodLabel(r.paymentMethod) },
                { label: "Consent", value: r.consent ? "Yes" : "No" },
              ]}
            />
          )}
        </DrawerSection>
        <DrawerSection label="Fee">
          <Fields
            items={[
              {
                label: "Amount Due",
                wide: isTeamFeeShare(r),
                value: (
                  <>
                    <span className="rep-amount">{formatMoney(r.feeAmount, r.feeCurrency)}</span>
                    {isTeamFeeShare(r) && <span className="rep-cell-muted"> · included in the team fee</span>}
                  </>
                ),
              },
              r.feePrice != null && r.feePrice !== r.feeAmount && {
                label: "Fee Price",
                value: <>{formatMoney(r.feePrice, r.feeCurrency)}{r.feePerTeam ? " per team" : ""}</>,
              },
              !!r.feeDescription && { label: "Description", value: r.feeDescription, wide: true },
              !!r.promoCode && { label: "Promo Code", value: r.promoCode },
              !!r.fawryReferenceNumber && {
                label: "Fawry Ref",
                value: <span className="rep-cell-mono">{r.fawryReferenceNumber}</span>,
              },
            ]}
          />
        </DrawerSection>
        {(r.nationality || r.gender) && (
          <DrawerSection label="Player">
            <Fields
              items={[
                !!r.nationality && { label: "Nationality", value: r.nationality },
                !!r.gender && { label: "Gender", value: r.gender },
              ]}
            />
          </DrawerSection>
        )}
        {r.receiptScreenshot && (
          <DrawerSection label="Receipt">
            <ReceiptButton url={r.receiptScreenshot} onPreview={onPreviewReceipt} />
          </DrawerSection>
        )}
        <DrawerSection label="Timeline">
          <Fields items={[{ label: "Registered", value: fmtCairoDateTime(r.addedAt) }]} />
        </DrawerSection>
      </>
    );
    // Edit/Save/Cancel for the three status fields, plus a WhatsApp deep
    // link when a phone is on file. Edit mode swaps the WhatsApp button out
    // so the action area stays focused on the in-progress edit.
    const wa = whatsappLink(r.phoneNumber);
    const handleSave = async () => {
      setSavingEdit(true);
      try {
        await onUpdateRegistration(r.teamId, r.teamMemberId, {
          status: editStatus,
          paymentStatus: editPaymentStatus,
          paymentMethod: lockedMethod ? undefined : (editPaymentMethod === "" ? null : editPaymentMethod),
        });
        setIsEditing(false);
        onClose();
      } catch {
        // Error already surfaced as a toast by the parent; keep the form open.
      } finally {
        setSavingEdit(false);
      }
    };
    actions = isEditing ? (
      <>
        <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={savingEdit}>
          Cancel
        </Button>
        <Button icon={<Check size={14} />} onClick={handleSave} loading={savingEdit}>
          {savingEdit ? "Saving..." : "Save changes"}
        </Button>
      </>
    ) : (
      <>
        {wa && (
          <a className={buttonClassName({ variant: "ghost" })} href={wa} target="_blank" rel="noopener noreferrer">
            <span className="ui-btn__icon"><MessageCircle size={14} /></span>
            Message on WhatsApp
          </a>
        )}
        <Button icon={<Pencil size={14} />} onClick={() => setIsEditing(true)}>
          Edit registration
        </Button>
      </>
    );
  } else if (selection.kind === "payment") {
    const r = selection.row;
    eyebrow = `Payment · ${r.source === "Balance" ? "Wallet" : r.source}`;
    title = r.playerName;
    subtitle = [r.teamName, r.categoryName].filter(Boolean).join(" · ") || undefined;
    body = (
      <>
        <DrawerSection label="Amount">
          <Fields
            items={[
              { label: "Total", value: <span className="rep-amount">{formatMoney(r.amount, r.currency)}</span> },
              { label: "Source", value: <SourceTag source={r.source} /> },
              !!r.paymentMethod && { label: "Method", value: r.paymentMethod },
              r.payForTeam && {
                label: "Coverage",
                value: (
                  <>
                    Whole team
                    {r.teamMemberCount && r.teamMemberCount > 1 ? ` · ${r.teamMemberCount} players` : ""}
                  </>
                ),
              },
            ]}
          />
        </DrawerSection>
        <DrawerSection label="Status">
          <Fields
            items={[
              { label: "Payment", value: <StatusBadge status={r.paymentStatus} /> },
              !!r.registrationStatus && { label: "Registration", value: <StatusBadge status={r.registrationStatus} /> },
            ]}
          />
        </DrawerSection>
        {(r.reference || r.receiptScreenshot) && (
          <DrawerSection label="Reference">
            {r.reference && (
              <Fields items={[{ label: "Ref number", value: <span className="rep-cell-mono">{r.reference}</span> }]} />
            )}
            {r.receiptScreenshot && (
              <div className={r.reference ? "rep-drawer-gap" : undefined}>
                <ReceiptButton url={r.receiptScreenshot} onPreview={onPreviewReceipt} />
              </div>
            )}
          </DrawerSection>
        )}
        <DrawerSection label="Timeline">
          <Fields items={[{ label: "Created", value: fmtCairoDateTime(r.createdAt) }]} />
        </DrawerSection>
      </>
    );
    // Only InstaPay payments still pending need a manual confirm — Fawry is
    // settled by the gateway, balance is deducted server-side at registration.
    if (r.source === "InstaPay" && r.paymentStatus === "Pending") {
      actions = (
        <Button icon={<Check size={14} />} onClick={() => onRequestConfirm(r)} loading={confirmingId === r.id}>
          {confirmingId === r.id ? "Confirming..." : "Confirm payment"}
        </Button>
      );
    }
  } else {
    const r = selection.row;
    if (r.source === "Manual") {
      eyebrow = "Expense · Manual entry";
      title = r.categoryName || "Expense";
      subtitle = r.expenseDate ? fmtCairoDate(r.expenseDate) : undefined;
      body = (
        <>
          <DrawerSection label="Details">
            <Fields
              items={[
                { label: "Category", value: r.categoryName || "-" },
                { label: "Date", value: fmtCairoDate(r.expenseDate) },
                { label: "Amount", value: <span className="rep-amount rep-amount--expense">{formatEGP(r.cost)}</span> },
              ]}
            />
          </DrawerSection>
          {r.description && (
            <DrawerSection label="Description">
              <p className="rep-drawer-text">{r.description}</p>
            </DrawerSection>
          )}
        </>
      );
    } else {
      const sameDay = fmtCairoDate(r.startTime) === fmtCairoDate(r.endTime);
      eyebrow = "Expense · Court reservation";
      title = r.courtName || "-";
      subtitle = sameDay ? fmtCairoDate(r.startTime) : `${fmtCairoDate(r.startTime)} – ${fmtCairoDate(r.endTime)}`;
      body = (
        <>
          <DrawerSection label="Reservation">
            <Fields
              items={[
                { label: "Start", value: fmtCairoDateTime(r.startTime) },
                { label: "End", value: fmtCairoDateTime(r.endTime) },
                { label: "Duration", value: r.startTime && r.endTime ? formatHours(r.startTime, r.endTime) : "-" },
                { label: "Status", value: r.status ? <StatusBadge status={r.status} /> : "-" },
              ]}
            />
          </DrawerSection>
          <DrawerSection label="Cost">
            <Fields
              items={[
                { label: "Cost", value: <span className="rep-amount rep-amount--expense">{formatEGP(r.cost)}</span> },
                !!r.paymentMethod && { label: "Method", value: r.paymentMethod },
              ]}
            />
          </DrawerSection>
          {r.notes && (
            <DrawerSection label="Notes">
              <p className="rep-drawer-text">{r.notes}</p>
            </DrawerSection>
          )}
        </>
      );
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      placement="side"
      size="md"
      busy={savingEdit}
      className="rep-drawer"
      title={
        <>
          <span className="rep-drawer-eyebrow">{eyebrow}</span>
          {title}
        </>
      }
      subtitle={subtitle}
      footer={actions || undefined}
    >
      {body}
    </Dialog>
  );
};

const slugify = (s: string) =>
  (s || "tournament").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "tournament";

const TableToolbar = ({
  searchValue,
  onSearchChange,
  onExport,
  count,
  label,
  actions,
  children,
}: {
  searchValue: string;
  onSearchChange: (v: string) => void;
  onExport: () => void;
  count: number;
  label: string;
  /** Buttons after Export */
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <Toolbar
    className="rep-toolbar"
    actions={
      <>
        <span className="rep-toolbar-count">{count.toLocaleString()} {label}{count === 1 ? "" : "s"}</span>
        <Button variant="tint" icon={<Download size={14} />} onClick={onExport} disabled={count === 0}>
          Export
        </Button>
        {actions}
      </>
    }
  >
    <SearchInput
      className="rep-search"
      value={searchValue}
      onChange={onSearchChange}
      placeholder="Search..."
      aria-label={`Search ${label}s`}
    />
    {children}
  </Toolbar>
);

// The loading and empty rows of a report table.
const TableState = ({ colSpan, loading, icon, empty }: { colSpan: number; loading: boolean; icon: React.ReactNode; empty: string }) => (
  <tr className="rep-table-state">
    <td colSpan={colSpan}>
      {loading ? <SkeletonRows rows={6} columns={5} /> : <EmptyState compact icon={icon} title={empty} />}
    </td>
  </tr>
);

const RegistrationsPanel = ({
  rows, loading, searchValue, onSearchChange,
  statusFilter, onStatusChange, paymentFilter, onPaymentChange,
  categoryFilter, onCategoryChange, categoryOptions,
  onExport,
  onRowClick,
  activeId,
}: {
  rows: RegistrationRow[];
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  statusFilter: string;
  onStatusChange: (v: string) => void;
  paymentFilter: string;
  onPaymentChange: (v: string) => void;
  categoryFilter: string;
  onCategoryChange: (v: string) => void;
  categoryOptions: string[];
  onExport: () => void;
  onRowClick: (row: RegistrationRow) => void;
  activeId: string | null;
}) => (
  <>
    <TableToolbar
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      onExport={onExport}
      count={rows.length}
      label="registration"
    >
      <Select
        className="rep-filter-select"
        aria-label="Category"
        value={categoryFilter}
        onChange={(e) => onCategoryChange(e.target.value)}
      >
        <option value="">All categories</option>
        {categoryOptions.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </Select>
      <Select
        className="rep-filter-select"
        aria-label="Registration status"
        value={statusFilter}
        onChange={(e) => onStatusChange(e.target.value)}
      >
        <option value="">All statuses</option>
        <option value="Confirmed">Confirmed</option>
        <option value="Pending Payment">Pending Payment</option>
        <option value="Pending Consent">Pending Consent</option>
        <option value="Pending Invite">Pending Invite</option>
        <option value="Canceled">Canceled</option>
      </Select>
      <Select
        className="rep-filter-select"
        aria-label="Payment status"
        value={paymentFilter}
        onChange={(e) => onPaymentChange(e.target.value)}
      >
        <option value="">All payments</option>
        <option value="Paid">Paid</option>
        <option value="Pending">Pending</option>
      </Select>
    </TableToolbar>
    <TableWrap className="rep-table-wrap">
      <table className="ui-table rep-table">
        <thead>
          <tr>
            <th>Player</th>
            <th>Team</th>
            <th>Category</th>
            <th>Registration</th>
            <th>Payment</th>
            <th>Fee</th>
            <th>Method</th>
            <th>Promo</th>
            <th>Registered</th>
          </tr>
        </thead>
        <tbody>
          {loading || rows.length === 0 ? (
            <TableState colSpan={9} loading={loading} icon={<Users />} empty="No registrations match the current filters." />
          ) : (
            rows.map((r) => (
              <tr key={r.teamMemberId} {...rowProps(() => onRowClick(r), activeId === r.teamMemberId)}>
                <td>
                  <span className="rep-cell-strong">
                    {r.playerName}
                    {r.isCaptain && <Tag tone="spark">Captain</Tag>}
                  </span>
                </td>
                <td>{r.teamName}</td>
                <td>{r.categoryName}</td>
                <td><StatusBadge status={r.registrationStatus} /></td>
                <td><StatusBadge status={r.paymentStatus} /></td>
                <td>
                  <div className="rep-cell-stack">
                    <span className="rep-cell-fee">
                      {r.feePrice != null && r.feePrice > r.feeAmount && !isTeamFeeShare(r) && (
                        <span className="rep-cell-strike">{formatMoney(r.feePrice, r.feeCurrency)}</span>
                      )}
                      {formatMoney(r.feeAmount, r.feeCurrency)}
                    </span>
                    {r.feeDescription && (
                      <span className="rep-cell-muted">
                        {r.feeDescription}
                        {r.feePerTeam ? " · per team" : ""}
                      </span>
                    )}
                    {isTeamFeeShare(r) && <span className="rep-cell-muted">Included in the team fee</span>}
                  </div>
                </td>
                <td>{r.paymentMethod || <span className="rep-cell-muted">-</span>}</td>
                <td>{r.promoCode || <span className="rep-cell-muted">-</span>}</td>
                <td>
                  <div className="rep-cell-stack">
                    <span>{fmtCairoDate(r.addedAt)}</span>
                    <span className="rep-cell-muted">{r.addedAt ? moment(r.addedAt).fromNow() : ""}</span>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </TableWrap>
  </>
);

const PaymentsPanel = ({
  rows, loading, searchValue, onSearchChange,
  sourceFilter, onSourceChange,
  statusFilter, onStatusChange,
  onExport,
  onRowClick,
  activeId,
  onPreviewReceipt,
}: {
  rows: PaymentRow[];
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  sourceFilter: "" | PaymentSource;
  onSourceChange: (v: "" | PaymentSource) => void;
  statusFilter: string;
  onStatusChange: (v: string) => void;
  onExport: () => void;
  onRowClick: (row: PaymentRow) => void;
  activeId: string | null;
  onPreviewReceipt: (url: string) => void;
}) => (
  <>
    <TableToolbar
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      onExport={onExport}
      count={rows.length}
      label="payment"
    >
      <Select
        className="rep-filter-select"
        aria-label="Source"
        value={sourceFilter}
        onChange={(e) => onSourceChange(e.target.value as "" | PaymentSource)}
      >
        <option value="">All sources</option>
        <option value="Online">Online (Fawry)</option>
        <option value="InstaPay">InstaPay</option>
        <option value="Balance">Wallet Balance</option>
        <option value="Cash">Cash</option>
      </Select>
      <Select
        className="rep-filter-select"
        aria-label="Payment status"
        value={statusFilter}
        onChange={(e) => onStatusChange(e.target.value)}
      >
        <option value="">All statuses</option>
        <option value="Paid">Paid</option>
        <option value="Pending">Pending</option>
        <option value="Initiated">Initiated</option>
        <option value="Failed">Failed</option>
        <option value="Expired">Expired</option>
        <option value="Cancelled">Cancelled</option>
        <option value="Refunded">Refunded</option>
      </Select>
    </TableToolbar>
    <TableWrap className="rep-table-wrap">
      <table className="ui-table rep-table">
        <thead>
          <tr>
            <th>Player / Team</th>
            <th>Source</th>
            <th>Category</th>
            <th className="ui-table__num">Amount</th>
            <th>Payment</th>
            <th>Registration</th>
            <th>Method</th>
            <th>Receipt / Ref</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {loading || rows.length === 0 ? (
            <TableState colSpan={9} loading={loading} icon={<CreditCard />} empty="No payments for this tournament." />
          ) : (
            rows.map((r) => (
              <tr key={r.id} {...rowProps(() => onRowClick(r), activeId === r.id)}>
                <td>
                  <div className="rep-cell-stack">
                    <span className="rep-cell-strong">{r.playerName}</span>
                    {r.teamName && r.teamName !== r.playerName && (
                      <span className="rep-cell-muted rep-cell-team">
                        {r.teamName}
                        {r.payForTeam && (
                          <Tag tone="neutral">
                            {r.teamMemberCount && r.teamMemberCount > 1 ? `Paid for ${r.teamMemberCount}` : "Entire Team"}
                          </Tag>
                        )}
                      </span>
                    )}
                  </div>
                </td>
                <td><SourceTag source={r.source} /></td>
                <td>{r.categoryName || <span className="rep-cell-muted">-</span>}</td>
                <td className="ui-table__num"><span className="rep-amount">{formatMoney(r.amount, r.currency)}</span></td>
                <td><StatusBadge status={r.paymentStatus} /></td>
                <td><StatusBadge status={r.registrationStatus} /></td>
                <td>{r.paymentMethod || <span className="rep-cell-muted">-</span>}</td>
                <td>
                  {r.receiptScreenshot ? (
                    <IconButton
                      variant="tint"
                      size="sm"
                      label="Preview receipt"
                      icon={<ImageIcon size={15} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreviewReceipt(r.receiptScreenshot!);
                      }}
                    />
                  ) : r.reference ? (
                    <span className="rep-cell-mono">{r.reference}</span>
                  ) : (
                    <span className="rep-cell-muted">-</span>
                  )}
                </td>
                <td>
                  <div className="rep-cell-stack">
                    <span>{fmtCairoDate(r.createdAt)}</span>
                    <span className="rep-cell-muted">{moment(r.createdAt).fromNow()}</span>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </TableWrap>
  </>
);

const ExpensesPanel = ({
  rows, loading, searchValue, onSearchChange,
  statusFilter, onStatusChange,
  onExport,
  onRowClick,
  activeId,
  canManage,
  onAddExpense,
  onEditExpense,
  onDeleteExpense,
}: {
  rows: ExpenseRow[];
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  statusFilter: string;
  onStatusChange: (v: string) => void;
  onExport: () => void;
  onRowClick: (row: ExpenseRow) => void;
  activeId: string | null;
  canManage: boolean;
  onAddExpense: () => void;
  onEditExpense: (row: ExpenseRow) => void;
  onDeleteExpense: (row: ExpenseRow) => void;
}) => (
  <>
    <TableToolbar
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      onExport={onExport}
      count={rows.length}
      label="expense"
      actions={
        canManage && (
          <Button icon={<Plus size={14} />} onClick={onAddExpense}>
            Add expense
          </Button>
        )
      }
    >
      <Select
        className="rep-filter-select"
        aria-label="Status"
        value={statusFilter}
        onChange={(e) => onStatusChange(e.target.value)}
      >
        <option value="">All statuses</option>
        <option value="Confirmed">Confirmed</option>
        <option value="Pending">Pending</option>
        <option value="Cancelled">Cancelled</option>
      </Select>
    </TableToolbar>
    <TableWrap className="rep-table-wrap">
      <table className="ui-table rep-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Item</th>
            <th>Date</th>
            <th className="ui-table__num">Cost</th>
            <th>Status</th>
            <th>Method</th>
            <th>Notes</th>
            {canManage && <th className="ui-table__actions"><span className="ui-visually-hidden">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {loading || rows.length === 0 ? (
            <TableState
              colSpan={canManage ? 8 : 7}
              loading={loading}
              icon={<Receipt />}
              empty="No expenses recorded for this tournament."
            />
          ) : (
            rows.map((r) => {
              const isManual = r.source === "Manual";
              const itemName = isManual
                ? (r.description || "(no description)")
                : (r.courtName || "-");
              const dateStr = isManual
                ? fmtCairoDate(r.expenseDate)
                : r.startTime ? fmtCairoDateTime(r.startTime) : "-";
              return (
                <tr key={r.id} {...rowProps(() => onRowClick(r), activeId === r.id)}>
                  <td><span className="rep-cell-strong">{r.categoryName || "-"}</span></td>
                  <td>
                    <div className="rep-cell-stack">
                      <span>{itemName}</span>
                      {!isManual && r.startTime && r.endTime && (
                        <span className="rep-cell-muted">
                          {formatHours(r.startTime, r.endTime)} · until {fmtCairoDateTime(r.endTime).split(", ").pop()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{dateStr}</td>
                  <td className="ui-table__num"><span className="rep-amount rep-amount--expense">{formatEGP(r.cost)}</span></td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>{r.paymentMethod || <span className="rep-cell-muted">-</span>}</td>
                  <td>{r.notes || <span className="rep-cell-muted">-</span>}</td>
                  {canManage && (
                    <td className="ui-table__actions" onClick={(e) => e.stopPropagation()}>
                      {isManual ? (
                        <>
                          <IconButton size="sm" label="Edit expense" icon={<Pencil size={14} />} onClick={() => onEditExpense(r)} />
                          <IconButton
                            size="sm"
                            variant="danger"
                            label="Delete expense"
                            icon={<Trash size={14} />}
                            onClick={() => onDeleteExpense(r)}
                          />
                        </>
                      ) : (
                        <span className="rep-cell-muted">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </TableWrap>
  </>
);

// ── Manual expense form (Add/Edit) ──
// A side panel like the detail drawer, so all expense interactions feel consistent.
const EXPENSE_FORM_ID = "rep-expense-form";

const ExpenseFormDialog = ({
  open, mode, initial, categories, busy, onCancel, onSave,
}: {
  open: boolean;
  mode: "create" | "edit";
  initial?: ExpenseRow;
  categories: ExpenseCategoryDTO[];
  busy: boolean;
  onCancel: () => void;
  onSave: (data: {
    categoryId: string;
    amount: number;
    description?: string;
    status?: string;
    paymentMethod?: string;
    expenseDate?: string;
  }) => void;
}) => {
  const [categoryId, setCategoryId] = useState<string>(initial?.categoryId || categories[0]?.id || "");
  const [amount, setAmount] = useState<string>(initial ? String(initial.cost) : "");
  const [description, setDescription] = useState<string>(initial?.description || "");
  const [status, setStatus] = useState<string>(initial?.status || "");
  const [paymentMethod, setPaymentMethod] = useState<string>(initial?.paymentMethod || "");
  const [expenseDate, setExpenseDate] = useState<string>(
    initial?.expenseDate ? moment(initial.expenseDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")
  );
  const [errors, setErrors] = useState<{ category?: string; amount?: string }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryId) { setErrors({ category: "Pick a category" }); return; }
    const amt = Number(amount);
    if (!amt || amt <= 0) { setErrors({ amount: "Amount must be greater than zero" }); return; }
    setErrors({});
    onSave({
      categoryId,
      amount: amt,
      description: description.trim() || undefined,
      status: status.trim() || undefined,
      paymentMethod: paymentMethod.trim() || undefined,
      expenseDate: expenseDate ? new Date(expenseDate).toISOString() : undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      placement="side"
      size="sm"
      busy={busy}
      className="rep-drawer"
      title={
        <>
          <span className="rep-drawer-eyebrow">{mode === "create" ? "New entry" : "Editing entry"}</span>
          {mode === "create" ? "Add expense" : "Edit expense"}
        </>
      }
      subtitle="Manual entries appear alongside court reservations in this tournament's expense list."
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form={EXPENSE_FORM_ID} loading={busy} disabled={categories.length === 0}>
            {busy ? "Saving…" : mode === "create" ? "Add expense" : "Save changes"}
          </Button>
        </>
      }
    >
      {categories.length === 0 ? (
        <Alert tone="warn">No expense categories yet — add one in Configuration → Expense Categories first.</Alert>
      ) : (
        <form id={EXPENSE_FORM_ID} onSubmit={handleSubmit} noValidate>
          <FormGrid columns={1}>
            <Field label="Category" required error={errors.category}>
              {(id) => (
                <Select id={id} block value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={busy} invalid={!!errors.category}>
                  <option value="" disabled>Select category…</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Amount (EGP)" required error={errors.amount}>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={busy}
                  placeholder="0.00"
                  invalid={!!errors.amount}
                />
              )}
            </Field>

            <Field label="Date">
              {(id) => (
                <DateInput id={id} value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} disabled={busy} />
              )}
            </Field>

            <Field label="Status (optional)">
              {(id) => (
                <Select id={id} block value={status} onChange={(e) => setStatus(e.target.value)} disabled={busy}>
                  <option value="">— Not set —</option>
                  <option value="Paid">Paid</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled">Cancelled</option>
                </Select>
              )}
            </Field>

            <Field label="Payment method (optional)">
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  maxLength={100}
                  disabled={busy}
                  placeholder="e.g. Cash, Bank Transfer, Card"
                />
              )}
            </Field>

            <Field label="Description (optional)">
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                  disabled={busy}
                  placeholder="e.g. Trophies for the men's open final"
                />
              )}
            </Field>
          </FormGrid>
        </form>
      )}
    </Dialog>
  );
};

// The label/value summary under a confirm dialog's message.
const DialogMeta = ({ items }: { items: (DescriptionItem | false | null | undefined | "")[] }) => (
  <DescriptionList className="rep-dialog-meta" columns={1} items={items.filter(Boolean) as DescriptionItem[]} />
);

// ── Delete-expense confirmation ──
// The kit ConfirmDialog closes itself with `confirmed: true` after onConfirm resolves; the handler catches its own
// errors and clears the row only on success, so only a cancel (confirmed: false) is passed on — on a failure the
// dialog stays open.
const DeleteExpenseDialog = ({
  open, row, busy, onCancel, onConfirm,
}: {
  open: boolean;
  row: ExpenseRow;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}) => (
  <ConfirmDialog
    open={open}
    onClose={(confirmed) => { if (!confirmed) onCancel(); }}
    onConfirm={onConfirm}
    loading={busy}
    tone="danger"
    title={
      <>
        <span className="rep-drawer-eyebrow">Confirm deletion</span>
        Delete this expense?
      </>
    }
    heading="This action cannot be undone."
    confirmLabel={busy ? "Deleting…" : "Delete expense"}
    message={
      <>
        Removing this entry will recalculate the tournament's <strong>Expenses</strong> total
        and <strong>Net Revenue</strong>.
      </>
    }
  >
    <h3 className="rep-dialog-section-label">Expense</h3>
    <DialogMeta
      items={[
        { label: "Category", value: row.categoryName || "-" },
        { label: "Description", value: row.description || <span className="rep-cell-muted">(none)</span> },
        { label: "Date", value: fmtCairoDate(row.expenseDate) },
        { label: "Amount", value: <span className="rep-amount rep-amount--expense">{formatEGP(row.cost)}</span> },
      ]}
    />
  </ConfirmDialog>
);

// Admin-facing confirmation prompt before marking an InstaPay payment as Paid.
// Mirrors the dialog on the Transactions page so the flow looks the same.
const ConfirmPaymentDialog = ({
  open, row, busy, onCancel, onConfirm,
}: {
  open: boolean;
  row: PaymentRow;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}) => {
  const isTeamPayment = row.payForTeam && (row.teamMemberCount ?? 0) > 1;
  const subjectName = row.playerName || row.teamName || "this payment";
  return (
    <ConfirmDialog
      open={open}
      onClose={(confirmed) => { if (!confirmed) onCancel(); }}
      onConfirm={onConfirm}
      loading={busy}
      tone="primary"
      title="Confirm this payment?"
      confirmLabel={busy ? "Confirming..." : "Confirm payment"}
      message={
        isTeamPayment ? (
          <>
            This will mark <strong>{subjectName}</strong> and all <strong>{row.teamMemberCount} teammates</strong> on
            <strong> {row.teamName}</strong> as <strong>Paid</strong> and <strong>Confirmed</strong>. This cannot be undone from this screen.
          </>
        ) : (
          <>
            This will mark <strong>{subjectName}</strong> as <strong>Paid</strong> and set the registration status to <strong>Confirmed</strong>. This cannot be undone from this screen.
          </>
        )
      }
    >
      <DialogMeta
        items={[
          { label: "Team", value: <>{row.teamName}{isTeamPayment ? ` · ${row.teamMemberCount} players` : ""}</> },
          !!row.categoryName && { label: "Category", value: row.categoryName },
          {
            label: "Amount",
            value: (
              <>
                {formatMoney(row.amount, row.currency)}
                {isTeamPayment && <span className="rep-cell-muted rep-meta-note">· whole-team payment</span>}
              </>
            ),
          },
        ]}
      />
    </ConfirmDialog>
  );
};

export default Reports;
